import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash, CurrencyDollar, ArrowUUpLeft, MapPin } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, fdate, periodLabel } from "@/lib/format";
import { StatusBadge, EmptyState } from "@/components/common";
import { TextField, SelectField, AreaField } from "@/components/fields";
import { DocUploader } from "@/components/DocUploader";
import { fileUrl, SingleUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TIPOS_SVC = ["agua", "luz", "gas", "internet", "predial", "municipio", "seguridad", "mantenimiento", "otro"].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }));
const PERIOD = ["mensual", "bimestral", "trimestral", "semestral", "anual"].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }));
const RESP = [{ value: "arrendatario", label: "Arrendatario" }, { value: "propietario", label: "Propietario" }, { value: "inmobiliaria", label: "Inmobiliaria" }];
const TIPOS_APLIC = [["devolucion_total", "Devolución total"], ["devolucion_parcial", "Devolución parcial"], ["danos", "Descontar por arreglos/daños"], ["renta", "Aplicar a renta"], ["servicios", "Aplicar a servicios"], ["otros", "Otros adeudos"]].map(([value, label]) => ({ value, label }));

const Info = ({ label, value }) => (
  <div><div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div><div className="font-medium">{value || "—"}</div></div>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [owner, setOwner] = useState(null);
  const [contract, setContract] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [services, setServices] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const EMPTY_SVC = { property_id: id, tipo: "agua", nombre: "", periodicidad: "mensual", monto: "", tipo_monto: "fijo", fecha_corte: "", fecha_limite: "", responsable: "arrendatario", account_id: "", notas: "" };
  const [svcOpen, setSvcOpen] = useState(false);
  const [svc, setSvc] = useState(EMPTY_SVC);
  const [svcEdit, setSvcEdit] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [paySvc, setPaySvc] = useState(null);
  const [pay, setPay] = useState({ monto: "", account_id: "", nota: "" });

  const [depOpen, setDepOpen] = useState(false);
  const [dep, setDep] = useState({ importe: "", fecha_recepcion: new Date().toISOString().slice(0, 10), resguardo_tipo: "inmobiliaria", resguarda: "", account_id: "", nota: "" });
  const [aplicOpen, setAplicOpen] = useState(false);
  const [aplicDep, setAplicDep] = useState(null);
  const [aplic, setAplic] = useState({ tipo: "danos", monto: "", nota: "" });

  const [charges, setCharges] = useState([]);
  const [payOpen2, setPayOpen2] = useState(false);
  const [payCharge, setPayCharge] = useState(null);
  const [pago2, setPago2] = useState({ monto: "", banco_id: "", comprobante: "", nota: "" });

  const load = useCallback(async () => {
    const p = (await api.get(`/properties/${id}`)).data;
    setProp(p);
    if (p.owner_id) api.get(`/owners/${p.owner_id}`).then((r) => setOwner(r.data)).catch(() => {});
    const contracts = (await api.get("/contracts")).data.filter((c) => c.property_id === id);
    const active = contracts.find((c) => c.estado === "vigente") || contracts[0] || null;
    setContract(active);
    if (active?.tenant_id) api.get(`/tenants/${active.tenant_id}`).then((r) => setTenant(r.data)).catch(() => {});
    else setTenant(null);
    api.get("/services", { params: { property_id: id } }).then((r) => setServices(r.data));
    api.get("/deposits").then((r) => setDeposits(r.data.filter((d) => d.property_id === id)));
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/cobranza").then((r) => setCharges(r.data.filter((c) => c.property_id === id)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveDocs = async (docs) => {
    const body = { ...prop, documentos: docs };
    delete body.owner_nombre;
    await api.put(`/properties/${id}`, body);
    setProp(body);
    toast.success("Documentos actualizados");
  };

  const saveSvc = async () => {
    if (!svc.monto && svc.tipo_monto === "fijo") return toast.error("Indica el monto");
    try {
      const payload = { ...svc, property_id: id, monto: parseFloat(svc.monto) || 0, account_id: svc.account_id || null, fecha_corte: svc.fecha_corte || null, fecha_limite: svc.fecha_limite || null };
      if (svcEdit) await api.put(`/services/${svcEdit}`, payload); else await api.post("/services", payload);
      toast.success("Servicio guardado"); setSvcOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delSvc = async (sid) => { if (!window.confirm("¿Eliminar servicio?")) return; await api.delete(`/services/${sid}`); toast.success("Eliminado"); load(); };
  const savePay = async () => {
    if (!pay.monto || pay.monto <= 0) return toast.error("Monto inválido");
    try { await api.post(`/services/${paySvc.id}/pago`, { monto: parseFloat(pay.monto), account_id: pay.account_id || null, nota: pay.nota }); toast.success("Pago de servicio registrado"); setPayOpen(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveDep = async () => {
    if (!dep.importe || dep.importe <= 0) return toast.error("Importe inválido");
    try {
      await api.post("/deposits", { ...dep, importe: parseFloat(dep.importe), property_id: id, owner_id: prop.owner_id || null, tenant_id: contract?.tenant_id || null, contract_id: contract?.id || null, account_id: dep.account_id || null });
      toast.success("Depósito registrado"); setDepOpen(false); setDep({ importe: "", fecha_recepcion: new Date().toISOString().slice(0, 10), resguardo_tipo: "inmobiliaria", resguarda: "", account_id: "", nota: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openAplic = (d) => { setAplicDep(d); setAplic({ tipo: "danos", monto: "", nota: "" }); setAplicOpen(true); };
  const saveAplic = async () => {
    if (!aplic.monto || aplic.monto <= 0) return toast.error("Monto inválido");
    try { await api.post(`/deposits/${aplicDep.id}/aplicar`, { ...aplic, monto: parseFloat(aplic.monto) }); toast.success("Aplicación registrada. Se registró la salida del banco si había cuenta."); setAplicOpen(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openPay2 = (c) => { setPayCharge(c); setPago2({ monto: c.saldo ?? c.importe, banco_id: "", comprobante: "", nota: "" }); setPayOpen2(true); };
  const cobrarPrimerMes = async () => {
    if (!contract) return toast.error("Este inmueble no tiene contrato");
    try {
      const { data } = await api.post(`/cobranza/generar-contrato/${contract.id}`);
      const cs = (data.charges || []).filter((c) => c.property_id === id);
      setCharges(cs);
      const pend = cs.find((c) => ["pendiente", "parcial", "vencido"].includes(c.estado));
      if (pend) openPay2(pend);
      else toast.success("Todas las rentas están cobradas");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const savePay2 = async () => {
    if (!pago2.monto || pago2.monto <= 0) return toast.error("Importe inválido");
    try {
      await api.post(`/cobranza/${payCharge.id}/pago`, { monto: parseFloat(pago2.monto), banco_id: pago2.banco_id || null, comprobante: pago2.comprobante || null, nota: pago2.nota });
      toast.success("Pago registrado. Se calculó la comisión y el neto del propietario.");
      setPayOpen2(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!prop) return <div className="text-muted-foreground">Cargando…</div>;
  const comisionTxt = prop.comision_propietario_tipo === "fijo" ? money(prop.comision_propietario_valor) : `${prop.comision_propietario_valor || 0}%`;

  return (
    <div data-testid="property-detail-page">
      <Button variant="ghost" className="rounded-sm gap-2 mb-4 -ml-2" onClick={() => navigate("/inmuebles")}><ArrowLeft size={16} /> Inmuebles</Button>

      {/* Hero */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 rounded-sm overflow-hidden border border-border bg-card h-72 lg:h-96">
          {prop.foto ? (
            <img src={fileUrl(prop.foto)} alt={prop.nombre} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">Sin foto</div>
          )}
        </div>
        <div className="flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-display font-bold tracking-tight">{prop.nombre}</h1>
              <StatusBadge estado={prop.estado} />
            </div>
            <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={14} /> {prop.direccion || "—"} · {prop.ubicacion || ""}</p>
            <p className="text-sm mt-2 capitalize">{prop.tipo} · {prop.superficie || 0} m² · {prop.habitaciones || 0} rec · {(() => { const n = prop.banos || 0; const i = Math.floor(n); return `${i}${n - i >= 0.5 ? "½" : ""}`; })()} baños · {prop.estacionamientos || 0} est.</p>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="bg-card border border-border rounded-sm p-3"><div className="text-[10px] uppercase text-muted-foreground">Renta</div><div className="font-display font-bold tabular-nums">{money(prop.renta_solicitada)}</div></div>
            <div className="bg-card border border-border rounded-sm p-3"><div className="text-[10px] uppercase text-muted-foreground">Depósito</div><div className="font-display font-bold tabular-nums">{money(prop.deposito_solicitado)}</div></div>
            <div className="bg-card border border-border rounded-sm p-3"><div className="text-[10px] uppercase text-muted-foreground">Comisión</div><div className="font-display font-bold tabular-nums">{comisionTxt}</div></div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="propietario" data-testid="tab-propietario">Propietario</TabsTrigger>
          <TabsTrigger value="arrendatario" data-testid="tab-arrendatario">Arrendatario y contrato</TabsTrigger>
          <TabsTrigger value="servicios" data-testid="tab-servicios">Servicios</TabsTrigger>
          <TabsTrigger value="depositos" data-testid="tab-depositos">Depósito</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Info label="Características" value={prop.caracteristicas} />
            <Info label="Administrador" value={prop.administrador} />
            <Info label="Comisión al propietario" value={comisionTxt} />
          </div>
          <div className="bg-card border border-border rounded-sm p-6 mt-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Requisitos solicitados</div>
            <p className="whitespace-pre-wrap text-sm">{prop.requisitos || "Sin requisitos capturados. Edita el inmueble para agregarlos."}</p>
          </div>
        </TabsContent>

        <TabsContent value="propietario" className="mt-4">
          {owner ? (
            <div className="bg-card border border-border rounded-sm p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Info label="Nombre" value={owner.nombre} />
              <Info label="RFC" value={owner.rfc} />
              <Info label="Teléfono" value={owner.telefono} />
              <Info label="Correo" value={owner.email} />
              <Info label="Banco" value={owner.banco} />
              <Info label="CLABE" value={owner.clabe} />
              <div className="sm:col-span-2 lg:col-span-3"><Button variant="outline" className="rounded-sm" onClick={() => navigate(`/propietarios/${owner.id}`)}>Ver expediente del propietario</Button></div>
            </div>
          ) : <EmptyState title="Sin propietario vinculado" hint="Edita el inmueble para asignar un propietario." />}
        </TabsContent>

        <TabsContent value="arrendatario" className="mt-4">
          {contract ? (
            <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="font-display font-bold mb-3">Arrendatario</h3>
                {tenant ? (
                  <div className="grid grid-cols-2 gap-4">
                    <Info label="Nombre" value={tenant.nombre} />
                    <Info label="Teléfono" value={tenant.telefono} />
                    <Info label="Correo" value={tenant.email} />
                    <Info label="Identificación" value={tenant.identificacion} />
                    <div className="col-span-2"><Button variant="outline" className="rounded-sm" onClick={() => navigate(`/arrendatarios/${tenant.id}`)}>Ver expediente</Button></div>
                  </div>
                ) : <p className="text-sm text-muted-foreground">—</p>}
              </div>
              <div className="bg-card border border-border rounded-sm p-6">
                <div className="flex items-center gap-2 mb-3"><h3 className="font-display font-bold">Contrato</h3><StatusBadge estado={contract.estado} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <Info label="Renta mensual" value={money(contract.renta_mensual)} />
                  <Info label="Día de pago" value={contract.dia_pago} />
                  <Info label="Depósito" value={money(contract.deposito)} />
                  <Info label="Comisión" value={contract.comision?.tipo === "fijo" ? money(contract.comision?.valor) : `${contract.comision?.valor}%`} />
                  <Info label="Inicio" value={fdate(contract.fecha_inicio)} />
                  <Info label="Fin" value={fdate(contract.fecha_fin)} />
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-sm p-6 mt-4" data-testid="cobranza-panel">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="font-display font-bold">Cobranza de rentas</h3>
                <Button className="rounded-sm gap-2" data-testid="cobrar-primer-mes-btn" onClick={cobrarPrimerMes}><CurrencyDollar size={16} /> Cobrar primer mes de renta</Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Al cobrar el primer mes el sistema genera la renta automáticamente mes con mes. El neto al propietario ya descuenta la comisión configurada.</p>
              {charges.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aún no hay rentas generadas. Usa "Cobrar primer mes de renta".</p>
              ) : (
                <div className="border border-border rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader><TableRow><TableHead>Periodo</TableHead><TableHead>Vence</TableHead><TableHead className="text-right">Importe</TableHead><TableHead className="text-right">Comisión</TableHead><TableHead className="text-right">Neto propietario</TableHead><TableHead>Comprobante</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acción</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {charges.map((c) => {
                        const comp = (c.pagos || []).map((p) => p.comprobante).filter(Boolean).pop();
                        return (
                          <TableRow key={c.id} data-testid={`pd-charge-${c.id}`}>
                            <TableCell>{periodLabel(c.periodo)}</TableCell>
                            <TableCell className="text-xs">{fdate(c.fecha_vencimiento)}</TableCell>
                            <TableCell className="text-right tabular-nums">{money(c.importe)}</TableCell>
                            <TableCell className="text-right tabular-nums text-primary">{money(c.comision_importe)}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold text-[hsl(var(--accent))]">{money(c.neto_propietario)}</TableCell>
                            <TableCell>{comp ? <a href={fileUrl(comp)} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">Ver</a> : "—"}</TableCell>
                            <TableCell><StatusBadge estado={c.estado} /></TableCell>
                            <TableCell className="text-right">{["pendiente", "parcial", "vencido"].includes(c.estado) && <Button size="sm" className="rounded-sm gap-1" data-testid={`pd-cobrar-${c.id}`} onClick={() => openPay2(c)}><CurrencyDollar size={14} /> Cobrar</Button>}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
            </>
          ) : <EmptyState title="Sin contrato" hint="Este inmueble no tiene contrato registrado." action={<Button className="rounded-sm" onClick={() => navigate("/contratos")}>Crear contrato</Button>} />}
        </TabsContent>

        <TabsContent value="servicios" className="mt-4">
          <div className="flex justify-end mb-3"><Button data-testid="add-service-btn" className="rounded-sm gap-2" onClick={() => { setSvc(EMPTY_SVC); setSvcEdit(null); setSvcOpen(true); }}><Plus size={16} /> Agregar servicio</Button></div>
          {services.length === 0 ? (
            <EmptyState title="Sin servicios" hint="Agrega agua, luz, predial y otros servicios recurrentes del inmueble." />
          ) : (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Servicio</TableHead><TableHead>Periodicidad</TableHead><TableHead>Responsable</TableHead><TableHead>Corte / Límite</TableHead><TableHead className="text-right">Monto</TableHead><TableHead className="text-right">Pagado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
                <TableBody>
                  {services.map((s) => {
                    const pagado = (s.pagos || []).reduce((a, p) => a + (p.monto || 0), 0);
                    return (
                      <TableRow key={s.id} data-testid={`service-row-${s.id}`}>
                        <TableCell className="font-medium capitalize">{s.tipo}{s.nombre ? ` · ${s.nombre}` : ""}</TableCell>
                        <TableCell className="capitalize">{s.periodicidad}</TableCell>
                        <TableCell className="capitalize">{s.responsable}</TableCell>
                        <TableCell className="text-xs">{s.fecha_corte ? fdate(s.fecha_corte) : "—"} / {s.fecha_limite ? fdate(s.fecha_limite) : "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.tipo_monto === "variable" ? "Variable" : money(s.monto)}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(pagado)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" className="rounded-sm gap-1" data-testid={`pay-service-${s.id}`} onClick={() => { setPaySvc(s); setPay({ monto: s.monto || "", account_id: s.account_id || "", nota: "" }); setPayOpen(true); }}><CurrencyDollar size={14} /> Pagar</Button>
                          <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => { setSvc({ ...EMPTY_SVC, ...s }); setSvcEdit(s.id); setSvcOpen(true); }}>✎</Button>
                          <Button variant="ghost" size="icon" className="rounded-sm text-red-600" onClick={() => delSvc(s.id)}><Trash size={16} /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="depositos" className="mt-4">
          <div className="flex justify-end mb-3"><Button data-testid="add-deposit-btn" className="rounded-sm gap-2" onClick={() => setDepOpen(true)}><Plus size={16} /> Registrar depósito</Button></div>
          {deposits.length === 0 ? (
            <EmptyState title="Sin depósito" hint="Registra el depósito de garantía recibido para este inmueble." />
          ) : (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Recepción</TableHead><TableHead>Resguardo</TableHead><TableHead className="text-right">Importe</TableHead><TableHead className="text-right">En resguardo</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
                <TableBody>
                  {deposits.map((d) => (
                    <TableRow key={d.id} data-testid={`pd-deposit-${d.id}`}>
                      <TableCell className="text-xs">{fdate(d.fecha_recepcion)}</TableCell>
                      <TableCell className="capitalize">{d.resguardo_tipo || "—"}{d.account_nombre ? ` · ${d.account_nombre}` : ""}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(d.importe)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{money(d.saldo_resguardo)}</TableCell>
                      <TableCell><StatusBadge estado={d.estado} /></TableCell>
                      <TableCell className="text-right">
                        {d.estado !== "devuelto" && d.saldo_resguardo > 0 && <Button size="sm" variant="outline" className="rounded-sm gap-1" data-testid={`pd-aplicar-${d.id}`} onClick={() => openAplic(d)}><ArrowUUpLeft size={14} /> Aplicar</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="documentos" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl">
            <DocUploader value={prop.documentos || []} onChange={saveDocs} label="Documentos del inmueble" testId="property-doc-input" />
          </div>
        </TabsContent>
      </Tabs>

      {/* Service dialog */}
      <Dialog open={svcOpen} onOpenChange={setSvcOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{svcEdit ? "Editar servicio" : "Agregar servicio"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SelectField label="Tipo" value={svc.tipo} onChange={(v) => setSvc((s) => ({ ...s, tipo: v }))} testId="service-tipo" options={TIPOS_SVC} />
            <TextField label="Nombre / proveedor" value={svc.nombre} onChange={(v) => setSvc((s) => ({ ...s, nombre: v }))} />
            <SelectField label="Periodicidad" value={svc.periodicidad} onChange={(v) => setSvc((s) => ({ ...s, periodicidad: v }))} options={PERIOD} />
            <SelectField label="Tipo de monto" value={svc.tipo_monto} onChange={(v) => setSvc((s) => ({ ...s, tipo_monto: v }))} options={[{ value: "fijo", label: "Fijo" }, { value: "variable", label: "Variable" }]} />
            <TextField label="Monto" type="number" value={svc.monto} onChange={(v) => setSvc((s) => ({ ...s, monto: v }))} testId="service-monto" />
            <SelectField label="Responsable" value={svc.responsable} onChange={(v) => setSvc((s) => ({ ...s, responsable: v }))} testId="service-responsable" options={RESP} />
            <TextField label="Fecha de corte" type="date" value={svc.fecha_corte} onChange={(v) => setSvc((s) => ({ ...s, fecha_corte: v }))} />
            <TextField label="Fecha límite" type="date" value={svc.fecha_limite} onChange={(v) => setSvc((s) => ({ ...s, fecha_limite: v }))} />
            <SelectField label="Cuenta de pago (opcional)" value={svc.account_id} onChange={(v) => setSvc((s) => ({ ...s, account_id: v }))} options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} col={2} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setSvcOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-service-btn" onClick={saveSvc}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service pay dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Pagar servicio</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <TextField label="Monto" type="number" value={pay.monto} onChange={(v) => setPay((p) => ({ ...p, monto: v }))} testId="service-pay-monto" />
            <SelectField label="Cuenta de pago" value={pay.account_id} onChange={(v) => setPay((p) => ({ ...p, account_id: v }))} testId="service-pay-account" options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} />
            <TextField label="Nota" value={pay.nota} onChange={(v) => setPay((p) => ({ ...p, nota: v }))} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setPayOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-service-pay-btn" onClick={savePay}>Registrar pago</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deposit dialog */}
      <Dialog open={depOpen} onOpenChange={setDepOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>Registrar depósito de garantía</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="Importe" type="number" value={dep.importe} onChange={(v) => setDep((d) => ({ ...d, importe: v }))} testId="pd-deposit-importe" />
            <TextField label="Fecha de recepción" type="date" value={dep.fecha_recepcion} onChange={(v) => setDep((d) => ({ ...d, fecha_recepcion: v }))} />
            <SelectField label="Resguardo" value={dep.resguardo_tipo} onChange={(v) => setDep((d) => ({ ...d, resguardo_tipo: v }))} testId="pd-deposit-resguardo" options={[{ value: "inmobiliaria", label: "A resguardo de la inmobiliaria" }, { value: "propietario", label: "A resguardo del propietario" }]} col={2} />
            <TextField label="Quién lo resguarda (nombre)" value={dep.resguarda} onChange={(v) => setDep((d) => ({ ...d, resguarda: v }))} col={2} />
            <SelectField label="Cuenta donde se deposita (opcional)" value={dep.account_id} onChange={(v) => setDep((d) => ({ ...d, account_id: v }))} testId="pd-deposit-account" options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} col={2} />
            <AreaField label="Nota" value={dep.nota} onChange={(v) => setDep((d) => ({ ...d, nota: v }))} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setDepOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-pd-deposit-btn" onClick={saveDep}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aplicar dialog */}
      <Dialog open={aplicOpen} onOpenChange={setAplicOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Aplicar depósito</DialogTitle></DialogHeader>
          {aplicDep && <p className="text-sm text-muted-foreground">En resguardo: <span className="font-bold tabular-nums text-foreground">{money(aplicDep.saldo_resguardo)}</span></p>}
          <div className="grid gap-3 mt-2">
            <SelectField label="Tipo de aplicación" value={aplic.tipo} onChange={(v) => setAplic((a) => ({ ...a, tipo: v }))} testId="pd-aplic-tipo" options={TIPOS_APLIC} />
            <TextField label="Monto" type="number" value={aplic.monto} onChange={(v) => setAplic((a) => ({ ...a, monto: v }))} testId="pd-aplic-monto" />
            <TextField label="Nota" value={aplic.nota} onChange={(v) => setAplic((a) => ({ ...a, nota: v }))} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setAplicOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-pd-aplic-btn" onClick={saveAplic}>Aplicar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cobrar renta dialog */}
      <Dialog open={payOpen2} onOpenChange={setPayOpen2}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Cobrar renta</DialogTitle></DialogHeader>
          {payCharge && (
            <div className="text-sm space-y-1 mb-2">
              <p>{payCharge.property_nombre} · {periodLabel(payCharge.periodo)}</p>
              <p className="text-muted-foreground">Importe: <span className="font-bold tabular-nums text-foreground">{money(payCharge.importe)}</span> · Neto propietario: <span className="font-bold tabular-nums text-[hsl(var(--accent))]">{money(payCharge.neto_propietario)}</span></p>
            </div>
          )}
          <div className="grid gap-3">
            <TextField label="Monto" type="number" value={pago2.monto} onChange={(v) => setPago2((p) => ({ ...p, monto: v }))} testId="pd-pago-monto" />
            <SelectField label="Cuenta de depósito (opcional)" value={pago2.banco_id} onChange={(v) => setPago2((p) => ({ ...p, banco_id: v }))} testId="pd-pago-banco" options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} />
            <SingleUpload value={pago2.comprobante} onChange={(v) => setPago2((p) => ({ ...p, comprobante: v }))} testId="pd-pago-comprobante" />
            <TextField label="Nota" value={pago2.nota} onChange={(v) => setPago2((p) => ({ ...p, nota: v }))} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setPayOpen2(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-pd-pago-btn" onClick={savePay2}>Registrar pago</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
